import React, { useState } from 'react';
import { Text, ActionIcon, Tooltip, Badge, Group } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';

interface UsernameDisplayProps {
    username: string;
    variant?: 'table' | 'detail' | 'chip';
    showCopyButton?: boolean;
    maxLength?: number;
}

export const isProxyUsername = (username: string): boolean => {
    return username.startsWith('P-') || username.startsWith('p-');
};

export const formatProxyUsername = (username: string, maxLength: number = 15): string => {
    if (!isProxyUsername(username)) return username;

    if (username.length > maxLength) {
        const atIndex = username.indexOf('@');
        if (atIndex > 2) {
            const beforeAt = username.substring(0, atIndex);
            const last4BeforeAt = beforeAt.slice(-4);
            return `P-...${last4BeforeAt}`;
        } else {
            const suffix = username.slice(-4);
            return `P-...${suffix}`;
        }
    }

    return username;
};

import { copyToClipboard } from '../../utils/clipboardUtils';

export const UsernameDisplay: React.FC<UsernameDisplayProps> = ({
    username,
    variant = 'table',
    showCopyButton = true,
    maxLength = 15
}) => {
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);

    if (!username || username === '-') {
        return <Text size="sm">-</Text>;
    }

    const isProxy = isProxyUsername(username);
    const needsTruncation = username.length > maxLength;
    const displayUsername = expanded ? username : (needsTruncation ? formatProxyUsername(username, maxLength) : username);

    const handleCopy = async (event: React.MouseEvent) => {
        event.stopPropagation();
        const success = await copyToClipboard(username);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    if (variant === 'chip') {
        return (
            <Group gap={4} align="center" wrap="nowrap">
                <Badge
                    variant={isProxy ? "filled" : "outline"}
                    color={isProxy ? "blue" : "gray"}
                    size="sm"
                    ff="monospace"
                    fz="xs"
                    style={{
                        cursor: needsTruncation ? 'pointer' : 'default',
                        maxWidth: expanded ? 'none' : `${maxLength * 8}px`,
                        textTransform: 'none'
                    }}
                    onClick={needsTruncation ? handleExpand : undefined}
                    title={username}
                >
                    {displayUsername}
                </Badge>
                {showCopyButton && (
                    <Tooltip label={copied ? "Copied!" : "Copy Username"}>
                        <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                            <IconCopy size={14} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        );
    }

    if (variant === 'detail') {
        return (
            <Group gap="xs" align="center" wrap="nowrap">
                <Text
                    ff="monospace"
                    px="xs"
                    py={4}
                    style={{
                        backgroundColor: isProxy ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-default-hover)',
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: isProxy ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-default-border)',
                        wordBreak: 'break-all'
                    }}
                >
                    {username}
                </Text>
                {showCopyButton && (
                    <Tooltip label={copied ? "Copied!" : "Copy Username"}>
                        <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                            <IconCopy size={14} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        );
    }

    // Default table variant
    return (
        <Group gap={4} align="center" wrap="nowrap">
            <Text
                size="sm"
                ff="monospace"
                style={{
                    backgroundColor: isProxy ? 'var(--mantine-color-blue-light)' : 'transparent',
                    padding: isProxy ? '2px 4px' : '0',
                    borderRadius: isProxy ? '2px' : '0',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
                title={username}
            >
                {displayUsername}
            </Text>

            {showCopyButton && (
                <Tooltip label={copied ? "Copied!" : "Copy Username"}>
                    <ActionIcon variant="subtle" size="xs" onClick={handleCopy} color={copied ? "green" : "gray"}>
                        <IconCopy size={12} />
                    </ActionIcon>
                </Tooltip>
            )}
        </Group>
    );
};
